import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

import { displayError } from '@app/_utils/error-util';
import { FiscalReceiptDetails, FiscalReceiptItem } from '../../_models/fiscal-receipt-details.model';
import { FiscalReceiptsService } from '../../_services/fiscal-receipts.service';

export interface FiscalReceiptDetailDialogData {
  /** The iCash OperationID, from the row the operator clicked. */
  operationId: number;
  /** Shown in the title until the receipt arrives, and as a fallback if it has no fiscal number. */
  documentNumber: string;
}

/**
 * Shows one receipt's article lines.
 *
 * Only the header of a receipt travels with the list, so the lines are fetched when the dialog
 * opens rather than passed in.
 *
 * The total comes from the receipt header, not from adding the lines up: a discounted receipt
 * has line values that do not sum to what was actually charged, and the header figure is the
 * one printed on the бон and shown in the list behind this dialog.
 */
@Component({
  selector: 'app-fiscal-receipt-detail-dialog',
  templateUrl: './fiscal-receipt-detail-dialog.component.html',
  styleUrls: ['./fiscal-receipt-detail-dialog.component.css']
})
export class FiscalReceiptDetailDialogComponent implements OnInit {

  items: FiscalReceiptItem[] = [];
  receipt: FiscalReceiptDetails;

  dataLoaded = false;

  private errorMessageSubject = new Subject<string>();
  errorMessage$ = this.errorMessageSubject.asObservable();

  constructor(
    private dialogRef: MatDialogRef<FiscalReceiptDetailDialogComponent, void>,
    @Inject(MAT_DIALOG_DATA) public data: FiscalReceiptDetailDialogData,
    private translateService: TranslateService,
    private fiscalReceiptsService: FiscalReceiptsService
  ) { }

  ngOnInit(): void {
    this.loadReceipt();
  }

  /** The number printed on the бон, falling back to the document number the row showed. */
  get receiptNumber(): string {
    return this.receipt?.header?.fiscalNumber || this.data.documentNumber;
  }

  close() {
    this.dialogRef.close();
  }

  reload() {
    this.errorMessageSubject.next(undefined);
    this.loadReceipt();
  }

  private loadReceipt() {
    this.dataLoaded = false;

    this.fiscalReceiptsService.findReceipt(this.data.operationId).subscribe({
      next: receipt => {
        this.receipt = receipt;
        this.items = receipt?.items || [];
        this.dataLoaded = true;
      },
      error: err => {
        displayError(err);
        this.dataLoaded = false;
        this.errorMessageSubject.next(this.translateService.instant('messages.errorLoadingData'));
      }
    });
  }

}
